import { FC, useEffect } from 'react';
import { InnerhivePage } from '../InnerhivePage';
import { IonButton, IonButtons, IonToolbar } from '@ionic/react';
import { Text } from '../components/Text';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from '../caremap/CareMapState';
import { ArrowRightIcon, DuoFilePlusIcon } from '../theme/Icons';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { ICareMap } from '../caremap/ICareMap';
import { useBreakpoints } from '../theme/useBreakpoints';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { RemoveMeButton } from './RemoveMeButton';
import { CommunityFeedList } from './CommunityFeedList';
import { ICaremapNote } from '../notes/model/NotesClient';
import { AddCommunityPostWithQueryParamDrawer } from './AddCommunityPostWithQueryParamDrawer';
import { CommunityModelFactory } from '../share-caremap/CommunityModelFactory';
import { EditCommunityPostWithQueryParamDrawer } from './EditCommunityPostWIthQueryParamDrawer';

export const CommunityFeedPage: FC<{ careMapId: string; }> = ({ careMapId }) => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    return (
        <ActiveCareMapProviderV2 careMapId={careMapId}>
            <CommunityFeedPageWithCareMap careMapId={careMapId} />
        </ActiveCareMapProviderV2>
    );
};

const CommunityFeedPageWithCareMap: FC<{ careMapId: string; }> = ({ careMapId }) => {
    const history = useInstance(HistoryViewModel);
    const careMapState = useInstance(CareMapState);

    const addPostDrawer = useInstance(AddCommunityPostWithQueryParamDrawer);
    const editNoteDrawer = useInstance(EditCommunityPostWithQueryParamDrawer);

    const factory = useInstance(CommunityModelFactory);
    useSubscription(() => factory);
    const community = factory.forActiveCaremap();

    const notesModelFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesModelFactory);

    const { isAboveBreakpoint } = useBreakpoints();

    // load data on mount
    useEffect(() => {
        community.load();
    }, [community]);

    const activeCaremap = careMapState.getActiveCaremap();
    if (!activeCaremap){
        return null;
    }

    const careMap = careMapState.getActiveCaremap() as ICareMap;
    const notes = notesModelFactory.forActiveCaremap().getNotes();

    const hasPermissionsLink = Boolean(careMap.links.permissions);
    const hasCreateViewerAccessTokenLink = Boolean(careMap.links.createViewerAccessToken);

    const canEdit = activeCaremap.links.self.actions.includes('edit-notes');

    const editPost = (post: ICaremapNote) => {
        editNoteDrawer.open({ noteId: post.noteId });
    };

    const addPost = () => {
        addPostDrawer.open();
    };

    const collaboratorCount = community.getCollaboratorCount();
    const feedTitle = `${isAboveBreakpoint('sm') ?  'Innerhive Community' : `${careMapState.getPatientFirstName(careMap)}'s Innerhive Community`}`;
    const followers = community.isLoaded() && collaboratorCount ? `${collaboratorCount} Supporters` : '';

    return (
        <InnerhivePage
            data-id="community-feed-page"
            title={`${careMapState.getPatientFirstName(careMap)}'s Innerhive Community`}
        >
            <IonToolbar>
                <Text type="title">{feedTitle}</Text>

                <div
                    data-loaded={community.isLoaded()}
                    style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12}}
                >
                    {followers && (
                        <Text type="detail">
                            {`${followers}`}
                        </Text>
                    )}&nbsp;

                    <IonButtons slot="end">
                        {canEdit && (
                            <IonButton
                                fill="clear"
                                color="primary"
                                data-id="add-post-button"
                                onClick={addPost}
                            >
                                <DuoFilePlusIcon />&nbsp;Add Post
                            </IonButton>
                        )}

                        {hasPermissionsLink || hasCreateViewerAccessTokenLink ? (
                            <IonButton
                                fill="clear"
                                color="primary"
                                data-id="community-management-button"
                                onClick={() => history.push(`/community/${careMapId}`)}
                            >
                                {isAboveBreakpoint('sm') ? 'Manage Community' : 'Manage'}&nbsp;<ArrowRightIcon />
                            </IonButton>
                        ) : (
                            <RemoveMeButton />
                        )}
                    </IonButtons>
                </div>
            </IonToolbar>

            {notesModelFactory.forActiveCaremap().hasLoaded && (
                <CommunityFeedList
                    posts={notes}
                    editPostHandler={editPost}
                />
            )}
        </InnerhivePage>
    );
};
